// Hooks de TanStack Query de Administración · Mantenciones (plan
// 2026-10-30 §2.7). Claves propias bajo ["maintenance-items", …], al
// estilo de features/admin/queries.ts; cada escritura invalida además los
// paneles «Mantenciones programadas» de las tiendas
// (queryKeys.assetMaintenanceItems), porque cambiar la cadencia, el
// alcance o archivar un ítem cambia lo que cada tienda ve.

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { MaintenanceItemCreateBody, MaintenanceItemUpdateBody } from "@bitacorapro/api-types";
import { mantencionesApi } from "./api";

export const mantencionesKeys = {
  list: ["maintenance-items", "admin", "list"] as const,
  assets: (itemId: string) => ["maintenance-items", itemId, "assets"] as const,
} as const;

// queryKeys.assetMaintenanceItems(assetId) = ["assets", assetId,
// "maintenance-items"]: el id de tienda no se conoce aquí (un ítem de
// marca aplica a todas), así que se barren por forma, no por clave.
function invalidateStorePanels(qc: QueryClient): void {
  void qc.invalidateQueries({
    predicate: (q) => q.queryKey[0] === "assets" && q.queryKey[2] === "maintenance-items",
  });
}

// Deshabilitado para quien no administra el catálogo: el backend responde
// 403 a quien no pasa requireOrgAdmin (admin de plataforma, `admin` o el
// rol orgAdmin de la receta), igual que useChecklistTemplatesAdminList.
export function useMaintenanceItemsAdminList(enabled: boolean) {
  return useQuery({
    queryKey: mantencionesKeys.list,
    queryFn: () => mantencionesApi.list(),
    enabled,
  });
}

// Qué tiendas llevan un ítem. refetchOnMount "always" por la misma razón
// que useChecklistTemplateAssets: el PUT es un reemplazo completo y dos
// gerentes con el drawer abierto escribirían desde una lectura vieja.
export function useMaintenanceItemAssets(itemId: string | undefined) {
  return useQuery({
    queryKey: mantencionesKeys.assets(itemId ?? ""),
    queryFn: () => mantencionesApi.listAssets(itemId as string),
    enabled: !!itemId,
    refetchOnMount: "always",
  });
}

export function useCreateMaintenanceItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MaintenanceItemCreateBody) => mantencionesApi.create(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mantencionesKeys.list });
      invalidateStorePanels(qc);
    },
  });
}

export function usePatchMaintenanceItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: MaintenanceItemUpdateBody }) =>
      mantencionesApi.patch(itemId, body),
    onSuccess: (_data, { itemId }) => {
      void qc.invalidateQueries({ queryKey: mantencionesKeys.list });
      void qc.invalidateQueries({ queryKey: mantencionesKeys.assets(itemId) });
      invalidateStorePanels(qc);
    },
  });
}

// La respuesta ya es el conjunto nuevo: se siembra la caché del ítem y se
// invalida la lista por su columna «Aplica a» (assignedAssetCount).
export function usePutMaintenanceItemAssets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, assetIds }: { itemId: string; assetIds: string[] }) =>
      mantencionesApi.putAssets(itemId, assetIds),
    onSuccess: (data, { itemId }) => {
      qc.setQueryData(mantencionesKeys.assets(itemId), data);
      void qc.invalidateQueries({ queryKey: mantencionesKeys.list });
      invalidateStorePanels(qc);
    },
  });
}
